/**
 * Step 5 — Initial data synchronisation.
 *
 * Starts the main-process master-data pull, subscribes to its progress events,
 * and shows overall progress + the current task + a per-entity log. On failure it
 * offers a manual retry (the pull resumes from the last checkpoint).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import sx from '../setupStyles';
import { setupApi, apiBaseUrl } from '../desktopApi';

export default function Sync({ goNext, patchCtx }) {
  const [status, setStatus] = useState('running'); // running | done | failed
  const [overall, setOverall] = useState(0);
  const [task, setTask] = useState('Preparing…');
  const [log, setLog] = useState([]);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  const unsub = useRef(null);

  const run = useCallback(async () => {
    setStatus('running');
    setError('');
    try {
      const res = await setupApi.startSync(apiBaseUrl);
      if (!mounted.current) return;
      if (res && res.ok) {
        setOverall(100);
        setTask('Download complete');
        patchCtx({ syncResult: res });
        setStatus('done');
      } else {
        const failed = (res && res.failed ? res.failed : []).filter((f) => f.required);
        setError(`Could not download: ${failed.map((f) => f.entity).join(', ') || 'required data'}`);
        setStatus('failed');
      }
    } catch (err) {
      if (!mounted.current) return;
      setError(err && err.message ? err.message : 'Synchronisation failed');
      setStatus('failed');
    }
  }, [patchCtx]);

  useEffect(() => {
    mounted.current = true;
    unsub.current = setupApi.onProgress((p) => {
      if (!mounted.current || !p) return;
      if (typeof p.overall === 'number') setOverall(p.overall);
      if (p.currentTask) setTask(p.currentTask);
      if (p.phase === 'entity-done') {
        setLog((l) => [...l, { entity: p.entity, count: p.count, ok: true }]);
      } else if (p.phase === 'entity-failed') {
        setLog((l) => [...l, { entity: p.entity, error: p.error, ok: false, required: p.required }]);
      }
    });
    run();
    return () => {
      mounted.current = false;
      if (unsub.current) unsub.current();
    };
  }, [run]);

  return (
    <>
      <Box sx={sx.body}>
        <Stack spacing={1.5} sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Downloading initial data
          </Typography>

          <Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {task}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {overall}%
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={overall} sx={{ mt: 0.5 }} />
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 180, bgcolor: '#fafafa', borderRadius: 1 }}>
            <List dense>
              {log.map((row, i) => (
                <ListItem key={`${row.entity}-${i}`} sx={{ py: 0 }}>
                  <ListItemText
                    primary={`${row.ok ? '✓' : '✕'} ${row.entity}`}
                    secondary={row.ok ? `${row.count} records` : row.error}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      </Box>
      <Box sx={sx.footer}>
        <span />
        {status === 'failed' ? (
          <Button variant="contained" onClick={run}>
            Retry
          </Button>
        ) : (
          <Button variant="contained" onClick={goNext} disabled={status !== 'done'}>
            Continue
          </Button>
        )}
      </Box>
    </>
  );
}
